import { handleAuth } from 'boot/init-core/handle-auth.js'
import { handleNotAuth } from 'boot/init-core/handle-not-auth.js'
import { insertSiteInfo } from 'boot/init-core/insert-site-info.js'
import { RESTRICTION } from 'common/dictionary.js'
import { isWebView } from 'common/functions.js'
import { captureException } from 'common/sentry-client.js'
import { tokenService } from 'common/token-service.js'
import { reachCheckpoint } from 'hooks/usePerfStep.js'
import { useServerStatusStore } from 'stores/server-status/index.js'
import { useUserAuthStore } from 'src/stores/user/auth'
import { captureRegistrationCurrencySelectorAnomaly } from 'src/services/registration-observability.js'
import { getConfig } from '@/configs/index.js'

const config = getConfig()

const isProduction = process.env.NODE_ENV === 'production' && process.env.ENVIRONMENT === 'production'

export default async ({ router, urlPath }) => {
  reachCheckpoint('app.boot.init_core.start')

  const isBot = window.IS_BOT
  const AppReady = await window.watchGlobalVariable('AppReady')
  const serverStatusStore = useServerStatusStore()

  const [_dataSiteInfo, _dataRestrictions, dataAuth] = await Promise.all([
    AppReady.siteInfo
      .then((data) => {
        reachCheckpoint('app.boot.init_core.site_info')
        insertSiteInfo(data)
      })
      .catch((e) => {
        const siteInfoRequestError = e?.siteInfoRequestError ?? e
        captureRegistrationCurrencySelectorAnomaly({
          reason: 'site_info_request_failed',
          data: {
            siteInfoRequestError,
            siteInfoRequestNetworkContext: e?.siteInfoRequestNetworkContext ?? null,
            siteInfoRequestStatus: siteInfoRequestError?.status ?? null,
          },
        })
        if (siteInfoRequestError?.status === 401) {
          tokenService.removeToken()
          location.reload()
        }
        serverStatusStore.updateUnderDev(siteInfoRequestError?.status >= 500)
      }),
    AppReady.restrictions
      .then((data) => {
        reachCheckpoint('app.boot.init_core.restrictions')
        serverStatusStore.updateRestrictedGeo({
          country: data.country,
          client_ip: data.client_ip,
        })
        if (isBot) {
          serverStatusStore.updateRestricted(RESTRICTION.SERVICE_ALLOWED)
          return
        }
        serverStatusStore.updateRestricted(data.restricted)
      })
      .catch(() => {
        serverStatusStore.updateRestricted(RESTRICTION.SERVICE_ALLOWED)
      }),
    AppReady.auth
      .then(async (data) => {
        reachCheckpoint('app.boot.init_core.auth_start')
        const isAuth = !!data.token
        const isTelegramMiniAppAuth = data?.type === 'telegramMiniAppAuth'

        if (!isAuth && !isTelegramMiniAppAuth && !window.IS_BOT && !isWebView) {
          if (config.useMirrors && isProduction) {
            const route = router.resolve(urlPath)

            if (route.name !== 'auth-social') {
              const { siteInfo } = await AppReady.siteInfo

              const { mirrorDomain, domain } = siteInfo
              if (mirrorDomain && mirrorDomain !== domain) {
                const queryPath = encodeURIComponent(route.fullPath)
                const linkPath = route.fullPath ? '?path=' + queryPath : ''

                window.location.replace('https://' + mirrorDomain + linkPath)
                return
              }
            }
          }
        }

        const payload = {
          ...(data ?? {}),
          user: data?.user,
          token: data?.token,
          mirror: data?.mirror,
          type: data?.type,
          router,
          urlPath,
        }
        return handleAuth(payload)
      })
      .catch((e) => {
        console.error(e)
        if (e.status >= 500) {
          serverStatusStore.updateServerIsOk(false)
        } else {
          captureException(e)
        }
      }),
  ])

  delete window.AppReady

  const userAuthStore = useUserAuthStore()

  reachCheckpoint('app.boot.init_core.before_route')

  const routePath = dataAuth
  if (routePath) {
    return routePath
  }

  if (!userAuthStore.getAuth) {
    return handleNotAuth({ router, urlPath })
  }
}
